/**
 * StaffLists — P0-6 · Gestione LISTE CRM lato Staff (touch-friendly).
 *
 * Riutilizza gli STESSI endpoint /admin/crm/lists dell'Admin: Admin e Staff
 * sono due UI sullo stesso dominio CRM Lists (zero duplicazione entità).
 * Il permission model esistente `crm.customers.write` decide chi può
 * creare/modificare/aggiungere/rimuovere membership. Chi non ha il permesso
 * vede in read-only.
 *
 * Route:
 *   /staff/lists            → elenco liste + crea nuova
 *   /staff/lists/:listId    → drill-down clienti della lista (add/remove)
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Layers, Plus, Trash2, Archive, ArchiveRestore, Edit, Users, Search,
  ChevronRight, ArrowLeft, UserPlus, UserMinus,
} from "lucide-react";
import { hasPermission } from "@/lib/permissions";
import { useAuth } from "@/lib/auth";

const COLORS = [
  "#e08c3a", "#c04dcf", "#556b2f", "#4b7f52", "#365a9c",
  "#a94b6a", "#8b5cf6", "#0ea5e9", "#d4a017", "#6b7280",
];
const PAGE_SIZE = 50;

export default function StaffLists() {
  const { listId } = useParams();
  if (listId) return <StaffListDetail listId={listId} />;
  return <StaffListsIndex />;
}


// ===========================================================================
// INDEX · elenco liste
// ===========================================================================
function StaffListsIndex() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "crm.customers.write");
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (showArchived) params.set("include_archived", "true");
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/crm/lists?${params.toString()}`);
      setLists(r.data?.lists || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, showArchived]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const name = (editing?.name || "").trim();
    if (!name) { toast.error("Nome obbligatorio"); return; }
    try {
      if (editing?.new) {
        await api.post("/admin/crm/lists", {
          name, color: editing.color, description: editing.description || null });
        toast.success("Lista creata");
      } else {
        await api.patch(`/admin/crm/lists/${editing.id}`, {
          name, color: editing.color, description: editing.description || null });
        toast.success("Lista aggiornata");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doArchive = async (list, archived) => {
    try {
      await api.patch(`/admin/crm/lists/${list.id}`, { archived });
      toast.success(archived ? "Archiviata" : "Ripristinata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/crm/lists/${confirmDelete.id}`);
      toast.success("Lista eliminata");
      setConfirmDelete(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="staff-lists-page">
      <div>
        <div className="eyebrow">Servizio</div>
        <h1 className="font-serif text-3xl mt-1 flex items-center gap-2">
          <Layers className="w-7 h-7" /> Liste
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          Liste CRM condivise con Admin. Usale per segmentare gli invii WhatsApp.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca lista…" className="pl-9 h-11"
                  data-testid="staff-lists-search" />
        </div>
        <label className="flex items-center gap-1 text-xs text-ink-600">
          <input type="checkbox" checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  data-testid="staff-chk-archived" />
          Archiviate
        </label>
        {canWrite && (
          <Button size="sm"
                   onClick={() => setEditing({ new: true, name: "", color: COLORS[0], description: "" })}
                   data-testid="btn-staff-new-list"
                   className="bg-bronze-600 hover:bg-bronze-700 text-white rounded-full h-11 px-4">
            <Plus className="w-4 h-4 mr-1" /> Crea lista
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-ink-500 p-6">Caricamento…</div>
      ) : lists.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center"
              data-testid="staff-lists-empty">
          <Layers className="w-8 h-8 mx-auto text-ink-300 mb-2" />
          <div className="text-sm text-ink-500">Nessuna lista.</div>
          {canWrite && (
            <div className="text-xs text-ink-400 mt-1">
              Crea la prima con "Crea lista" (es. Stagionali, VIP, Staff, Fornitori…)
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="staff-lists-grid">
          {lists.map((l) => (
            <div key={l.id} data-testid={`staff-list-card-${l.id}`}
                  className={`rounded-2xl border-2 bg-white p-4 flex items-center gap-3 ${l.archived ? "opacity-60" : ""}`}
                  style={{ borderColor: l.color || "#e5e7eb" }}>
              <span className="w-5 h-5 rounded-full flex-shrink-0"
                     style={{ background: l.color || "#94a3b8" }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium truncate">{l.name}</h3>
                  {l.archived && (
                    <span className="text-[9px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-ink-100 text-ink-500">
                      Archiviata
                    </span>
                  )}
                </div>
                {l.description && (
                  <p className="text-xs text-ink-500 truncate">{l.description}</p>
                )}
                <div className="text-xs text-ink-600 mt-0.5">
                  <b data-testid={`staff-list-count-${l.id}`}>{l.members_count}</b> clienti
                </div>
              </div>
              <div className="flex gap-1">
                {canWrite && (
                  <>
                    <button onClick={() => setEditing({ ...l })}
                             data-testid={`btn-staff-edit-list-${l.id}`}
                             className="p-2 rounded-full hover:bg-sand-100 text-ink-600">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => doArchive(l, !l.archived)}
                             data-testid={`btn-staff-archive-list-${l.id}`}
                             className="p-2 rounded-full hover:bg-sand-100 text-ink-600">
                      {l.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setConfirmDelete(l)}
                             data-testid={`btn-staff-delete-list-${l.id}`}
                             className="p-2 rounded-full hover:bg-rose-50 text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                <Link to={`/staff/lists/${l.id}`}
                       data-testid={`btn-staff-open-list-${l.id}`}
                       className="p-2 rounded-full bg-bronze-50 hover:bg-bronze-100 text-bronze-700">
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing.new ? "Nuova lista" : "Modifica lista"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs eyebrow">Nome</label>
                <Input value={editing.name || ""}
                        onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                        data-testid="staff-input-list-name" autoFocus />
              </div>
              <div>
                <label className="text-xs eyebrow">Colore</label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {COLORS.map((c) => (
                    <button key={c}
                             onClick={() => setEditing((s) => ({ ...s, color: c }))}
                             data-testid={`staff-color-${c}`}
                             className={`w-8 h-8 rounded-full border-2 ${editing.color === c ? "border-ink-900" : "border-transparent"}`}
                             style={{ background: c }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs eyebrow">Descrizione (opzionale)</label>
                <Input value={editing.description || ""}
                        onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                        data-testid="staff-input-list-description" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
              <Button onClick={save} data-testid="btn-staff-save-list"
                       className="bg-bronze-600 text-white">
                {editing.new ? "Crea lista" : "Salva"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmDelete && (
        <Dialog open onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Elimina "{confirmDelete.name}"?</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-ink-600 space-y-2">
              <p>Verranno rimosse <b>{confirmDelete.members_count}</b> appartenenze cliente. I clienti NON vengono eliminati.</p>
              <p className="text-xs text-ink-400">Preferisci archiviare? L'archivio nasconde la lista senza cancellarla.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annulla</Button>
              <Button onClick={doDelete} data-testid="btn-staff-confirm-delete-list"
                       className="bg-rose-600 hover:bg-rose-700 text-white">Elimina</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ===========================================================================
// DETAIL · elenco membri della lista + add/remove
// ===========================================================================
function StaffListDetail({ listId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = hasPermission(user, "crm.customers.write");

  const [list, setList] = useState(null);
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => { setOffset(0); setHasMore(true); setMembers([]); },
             [q, reloadTick]);

  useEffect(() => {
    const ctrl = new AbortController();
    const isFirstPage = offset === 0;
    if (isFirstPage) setLoading(true); else setLoadingMore(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    api.get(`/admin/crm/lists/${listId}/members?${params.toString()}`,
             { signal: ctrl.signal })
      .then((r) => {
        setList(r.data?.list || null);
        setTotal(r.data?.total || 0);
        const page = r.data?.members || [];
        setMembers((prev) => {
          if (isFirstPage) return page;
          const seen = new Set(prev.map((x) => x.id));
          const merged = prev.slice();
          for (const p of page) if (!seen.has(p.id)) merged.push(p);
          return merged;
        });
        setHasMore(page.length === PAGE_SIZE);
      })
      .catch((e) => { if (!ctrl.signal.aborted) toast.error(formatApiError(e)); })
      .finally(() => {
        if (isFirstPage) setLoading(false); else setLoadingMore(false);
      });
    return () => ctrl.abort();
  }, [listId, q, offset, reloadTick]);

  const removeFromList = async (customerId) => {
    try {
      await api.delete(`/admin/crm/lists/${listId}/members`,
                        { data: { customer_ids: [customerId] } });
      toast.success("Rimosso");
      setReloadTick((t) => t + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="staff-list-detail">
      <div className="flex items-center gap-2">
        <button onClick={() => nav("/staff/lists")}
                 data-testid="btn-back-lists"
                 className="p-2 rounded-full hover:bg-sand-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="eyebrow">Lista CRM</div>
          <h1 className="font-serif text-2xl flex items-center gap-2 truncate">
            {list && (
              <span className="w-4 h-4 rounded-full flex-shrink-0"
                     style={{ background: list.color || "#94a3b8" }} />
            )}
            <span className="truncate">{list?.name || "…"}</span>
          </h1>
          <div className="text-sm text-ink-500">
            <b data-testid="staff-list-total">{total}</b> clienti
          </div>
        </div>
        {canWrite && (
          <Button size="sm" onClick={() => setAddOpen(true)}
                   data-testid="btn-add-customers-to-list"
                   className="bg-bronze-600 hover:bg-bronze-700 text-white rounded-full h-11 px-4">
            <UserPlus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        )}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca dentro la lista…" className="pl-9 h-11"
                data-testid="staff-list-members-search" />
      </div>

      {loading ? (
        <div className="text-ink-500 p-6">Caricamento…</div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center text-sm text-ink-500"
              data-testid="staff-list-members-empty">
          {q ? "Nessun cliente corrisponde alla ricerca." : "Nessun cliente in questa lista."}
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} data-testid={`staff-list-member-${m.id}`}
                  className="rounded-xl border border-sand-200 bg-white p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {[m.name, m.surname].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="text-xs text-ink-500 truncate">
                  {m.phone || "-"} {m.email && `· ${m.email}`}
                </div>
              </div>
              {canWrite && (
                <button onClick={() => removeFromList(m.id)}
                         data-testid={`btn-remove-from-list-${m.id}`}
                         className="p-2 rounded-full hover:bg-rose-50 text-rose-600">
                  <UserMinus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && members.length > 0 && hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" disabled={loadingMore}
                   onClick={() => setOffset((o) => o + PAGE_SIZE)}
                   data-testid="btn-staff-list-load-more"
                   className="rounded-full">
            {loadingMore ? "Caricamento…" : `Carica altri ${PAGE_SIZE}`}
          </Button>
        </div>
      )}

      {addOpen && (
        <AddCustomersToListModal listId={listId}
                                    onClose={() => setAddOpen(false)}
                                    onAdded={() => { setAddOpen(false); setReloadTick((t) => t + 1); }} />
      )}
    </div>
  );
}


// ===========================================================================
// AddCustomersToListModal · ricerca CRM + selezione multipla
// ===========================================================================
function AddCustomersToListModal({ listId, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "50");
    api.get(`/admin/customers?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [q]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const confirm = async () => {
    if (selected.size === 0) return;
    try {
      const r = await api.post(`/admin/crm/lists/${listId}/members`,
                                { customer_ids: Array.from(selected) });
      toast.success(`Aggiunti ${r.data?.added || 0} · già presenti ${r.data?.already_present || 0}`);
      onAdded?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="add-customers-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl max-w-md w-full p-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl">Aggiungi clienti</h3>
          <button onClick={onClose} className="opacity-60 hover:opacity-100">✕</button>
        </div>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca nome, telefono, email…"
                  className="pl-9 h-11"
                  data-testid="add-customers-search" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {loading && <div className="text-ink-400 text-sm p-2">Caricamento…</div>}
          {rows.map((r) => (
            <button key={r.id} onClick={() => toggle(r.id)}
                     data-testid={`add-customer-row-${r.id}`}
                     className={`w-full text-left rounded-xl border p-2.5 flex items-center gap-2 ${
                       selected.has(r.id) ? "border-emerald-500 bg-emerald-50" : "border-sand-200 hover:border-bronze-400"}`}>
              <input type="checkbox" checked={selected.has(r.id)} readOnly />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {[r.name, r.surname].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="text-xs text-ink-500 truncate">{r.phone || "-"}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="border-t border-sand-200 pt-3 mt-2 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={confirm} data-testid="btn-confirm-add-customers"
                   disabled={selected.size === 0}
                   className="bg-bronze-600 text-white">
            Aggiungi ({selected.size})
          </Button>
        </div>
      </div>
    </div>
  );
}
